import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
  commonZH, commonEN, commonJA,
  staticContentZH, staticContentEN, staticContentJA,
  modalZH, modalEN, modalJA,
  lineZH, lineEN, lineJA,
  resourceLimitZH, resourceLimitEN, resourceLimitJA
} from '@redpanda/common-locales';
import { deepMerge, createI18nInitConfig } from '@redpanda/utils';

// 本地翻譯檔案
import enTranslation from './locales/en/translation.json';
import zhTranslation from './locales/zh/translation.json';
import jaTranslation from './locales/ja/translation.json';
import enPages from './locales/en/pages.json';
import zhPages from './locales/zh/pages.json';
import jaPages from './locales/ja/pages.json';
import enStaticContent from './locales/en/static-content.json';
import zhStaticContent from './locales/zh/static-content.json';
import jaStaticContent from './locales/ja/static-content.json';
import enComponents from './locales/en/components.json';
import zhComponents from './locales/zh/components.json';
import jaComponents from './locales/ja/components.json';

// 將 common-locales 的結構轉換為 Booking 使用的扁平結構
function flattenCommonLocales(common: any) {
  return {
    // actions 展平
    add: common.actions?.add,
    edit: common.actions?.edit,
    delete: common.actions?.delete,
    save: common.actions?.save,
    cancel: common.actions?.cancel,
    confirm: common.actions?.confirm,
    view: common.actions?.view,
    viewDetails: common.actions?.viewDetails,
    close: common.actions?.close,
    back: common.actions?.back,
    search: common.actions?.search,
    filter: common.actions?.filter,
    reset: common.actions?.reset,
    submit: common.actions?.submit,
    update: common.actions?.update,
    create: common.actions?.create,
    refresh: common.actions?.refresh,
    // status 展平
    loading: common.status?.loading,
    saving: common.status?.saving,
    refreshing: common.status?.updating,
    // table columns 展平
    name: common.table?.columns?.name,
    phone: common.table?.columns?.phone,
    email: common.table?.columns?.email,
    status: common.table?.columns?.status,
    amount: common.table?.columns?.amount,
    actions: common.table?.columns?.actions,
    // messages 區塊
    messages: common.messages,
    noData: common.messages?.noData,
    noResults: common.messages?.noResults,
    confirmDelete: common.messages?.confirmDelete,
    // footer 區塊
    footer: common.footer,
    // 共用分頁元件 ListPagination 用 common.pagination.*
    pagination: common.pagination,
  };
}

function mergeTranslations(common: any, local: any) {
  const flattenedCommon = flattenCommonLocales(common);
  return {
    ...local,
    common: deepMerge(flattenedCommon, local.common || {}),
  };
}

// 將共用 LINE 翻譯 merge 到 pages 的 admin.settings.lineIntegration
function mergePagesWithLine(pages: any, line: any) {
  const merged = { ...pages };
  if (merged.admin?.settings?.lineIntegration) {
    merged.admin = {
      ...merged.admin,
      settings: {
        ...merged.admin.settings,
        lineIntegration: deepMerge(line, merged.admin.settings.lineIntegration),
      },
    };
  }
  return merged;
}

const resources = {
  en: {
    translation: mergeTranslations(commonEN, enTranslation),
    pages: mergePagesWithLine(enPages, lineEN),
    staticContent: deepMerge(staticContentEN, enStaticContent),
    components: enComponents,
    modal: modalEN,
    resourceLimit: resourceLimitEN,
  },
  zh: {
    translation: mergeTranslations(commonZH, zhTranslation),
    pages: mergePagesWithLine(zhPages, lineZH),
    staticContent: deepMerge(staticContentZH, zhStaticContent),
    components: zhComponents,
    modal: modalZH,
    resourceLimit: resourceLimitZH,
  },
  ja: {
    translation: mergeTranslations(commonJA, jaTranslation),
    pages: mergePagesWithLine(jaPages, lineJA),
    staticContent: deepMerge(staticContentJA, jaStaticContent),
    components: jaComponents,
    modal: modalJA,
    resourceLimit: resourceLimitJA,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(createI18nInitConfig({ resources }));

export default i18n;
